import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Modal, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import {
  GetHomeDataResponse,
  GetAllAssistantTodosResponse,
  GetUserTodosResponse,
  GetUserArtifactsResponse,
  SampleRoutine,
  AgentStreamEvents,
} from '@dreamai/types';
import { useAgent } from '../../contexts/AgentContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useMessaging } from '../../contexts/MessagingContext';
import { usePusher } from '../../contexts/PusherContext';
import { useResponseArtifactEvents } from '../../providers';
import ThreadViewContainer from './sidebar/ThreadViewContainer';
import { ChatCanvasProvider } from '../../contexts/ChatCanvasContext';
import {
  ThreadCallProvider,
  useThreadCallOptional,
} from '../../contexts/ThreadCallContext';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp-color.svg';
import { ReactComponent as CallingIcon } from '../../icons/webrtc-calls/calling.svg';
import api from '../../api';
import UniNav, { UniTab } from './UniNav';
import { isRepresentedInWaitingForYou } from './activityHelpers';
import WelcomeHeader from './WelcomeHeader';
import ProjectsSection from './ProjectsSection';
import RoutinesSection from './RoutinesSection';
import RoutineCreateView from './RoutineCreateView';
import RoutineDetailView from './RoutineDetailView';
import SampleRoutineDetailView from './SampleRoutineDetailView';
import DailyBriefingDrawer from './DailyBriefingDrawer';
import ActivityPage from './ActivityPage';
import RemindersPage from './RemindersPage';
import DocumentsPage from './DocumentsPage';
import { ReactComponent as SunSettingIcon } from '../../icons/sun-setting.svg';
import './uni-page.scss';

const UniPageHeaderCallButton: React.FC = () => {
  const threadCall = useThreadCallOptional();
  const { activeAgent } = useAgent();
  const { voiceCallingEnabled } = useFeatureFlags();
  if (!voiceCallingEnabled) return null;
  if (!threadCall?.canStartCall) return null;
  const label = `Call ${activeAgent?.nickname || activeAgent?.name || 'agent'}`;
  return (
    <button
      type='button'
      className='uni-page-chat-header-call-btn'
      onClick={threadCall.startCall}
      aria-label={label}
      title={label}
    >
      <CallingIcon />
    </button>
  );
};

const UniPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { activeAgent } = useAgent();
  const { messagingState } = useMessaging();
  const [homeData, setHomeData] = useState<GetHomeDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<UniTab>(
    () => (searchParams.get('tab') as UniTab) || 'home',
  );
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [regCodeLoading, setRegCodeLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [regCodeError, setRegCodeError] = useState<string | null>(null);

  // Tab counts
  const [activityCount, setActivityCount] = useState(0);
  const [remindersCount, setRemindersCount] = useState(0);

  // Nav turns into a glassy pill once the left panel scrolls
  const [navScrolled, setNavScrolled] = useState(false);
  const handleLeftScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrolled = e.currentTarget.scrollTop > 8;
    setNavScrolled((prev) => (prev === scrolled ? prev : scrolled));
  }, []);
  const [todoistConnected, setTodoistConnected] = useState(false);

  // Left-panel view — driven by URL params so nav tabs work normally
  const currentView = searchParams.get('view');
  const creatingRoutine = currentView === 'create-routine';
  const viewingRoutineDetail = currentView === 'routine-detail';
  const viewingSampleRoutine = currentView === 'sample-routine';
  const viewingDailyBriefing = currentView === 'daily-briefing';
  const routineAutopilotId = searchParams.get('autopilot_id') || '';
  const routineNameParam = searchParams.get('routine_name') || '';
  const cameFrom = searchParams.get('from') || '';

  // Held in state since SampleRoutine is an object (not serializable to URL params)
  const [selectedSampleRoutine, setSelectedSampleRoutine] =
    useState<SampleRoutine | null>(null);
  const [settingUp, setSettingUp] = useState(false);

  const handleTabChange = useCallback(
    (tab: UniTab) => {
      setActiveTab(tab);
      if (tab === 'home') {
        setSearchParams({});
      } else {
        setSearchParams({ tab });
      }
    },
    [setSearchParams],
  );

  const handleCreateRoutine = useCallback(() => {
    setActiveTab('home');
    setSearchParams({ view: 'create-routine' });
  }, [setSearchParams]);

  const handleCreateRoutineBack = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const handleRoutineCreated = useCallback(
    (autopilotId: string, routineName: string) => {
      setSearchParams({
        view: 'routine-detail',
        autopilot_id: autopilotId,
        routine_name: routineName,
      });
    },
    [setSearchParams],
  );

  const handleRoutineDetailBack = useCallback(() => {
    if (cameFrom === 'daily-briefing') {
      setSearchParams({ view: 'daily-briefing' });
    } else {
      setSearchParams({});
    }
  }, [setSearchParams, cameFrom]);

  const handleViewSampleRoutine = useCallback(
    (sampleRoutine: SampleRoutine) => {
      setSelectedSampleRoutine(sampleRoutine);
      setActiveTab('home');
      setSearchParams({ view: 'sample-routine', sample_id: sampleRoutine.id });
    },
    [setSearchParams],
  );

  const handleSampleRoutineBack = useCallback(() => {
    setSelectedSampleRoutine(null);
    setSearchParams({});
  }, [setSearchParams]);

  const handleToggleDailyBriefing = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get('view') === 'daily-briefing') {
        next.delete('view');
      } else {
        next.set('view', 'daily-briefing');
      }
      return next;
    });
  }, [setSearchParams]);

  const handleDailyBriefingClose = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('view');
      return next;
    });
  }, [setSearchParams]);

  const handleViewRoutineFromBriefing = useCallback(
    (autopilotId: string, routineName: string) => {
      setActiveTab('home');
      setSearchParams({
        view: 'routine-detail',
        autopilot_id: autopilotId,
        routine_name: routineName,
        from: 'daily-briefing',
      });
    },
    [setSearchParams],
  );

  const handleSetUpSampleRoutine = useCallback(
    async (sampleRoutine: SampleRoutine) => {
      setSettingUp(true);
      try {
        const token = await getAccessTokenSilently();
        await api.post(
          `/sample-routines/${sampleRoutine.id}/setup`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        // Stay on the page — the AI response will appear in the chat panel via Pusher.
        // settingUp remains true to keep the button disabled until the user navigates away.
      } catch (err) {
        console.error('Failed to set up sample routine:', err);
        setSettingUp(false);
      }
    },
    [getAccessTokenSilently, setSearchParams],
  );

  const handleConnectWhatsapp = useCallback(async () => {
    setShowWhatsappModal(true);
    setRegCodeLoading(true);
    setRegCodeError(null);
    setRegistrationData(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/agent-whatsapp/registration-code',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRegistrationData(response.data);
    } catch (err) {
      console.error('Error getting WhatsApp registration code:', err);
      setRegCodeError(
        'Failed to generate registration code. Please try again.',
      );
    } finally {
      setRegCodeLoading(false);
    }
  }, [getAccessTokenSilently]);

  // Close modal when WhatsApp becomes connected
  useEffect(() => {
    if (messagingState.whatsapp.connected && showWhatsappModal) {
      setShowWhatsappModal(false);
    }
  }, [messagingState.whatsapp.connected, showWhatsappModal]);

  const fetchHomeData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetHomeDataResponse>('/home/data', {
        params: activeAgent?.id ? { agent_id: activeAgent.id } : undefined,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setHomeData(response.data);
      }
    } catch (err) {
      console.error('Error fetching home data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, activeAgent?.id]);

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  // ─── Switch to documents tab when research_app_id is set (e.g. from ThreadView) ──
  useEffect(() => {
    if (searchParams.get('research_app_id') && activeTab !== 'documents') {
      setActiveTab('documents');
    }
  }, [searchParams]);

  // ─── Fetch tab counts ─────────────────────────────────────────

  // Activity count = in-flight todos + pending artifacts
  const fetchActivityCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const [todosRes, artifactsRes] = await Promise.all([
        api.get<GetAllAssistantTodosResponse>('/todos', { headers }),
        api.get<GetUserArtifactsResponse>(
          '/response-artifacts/user/artifacts?status=pending',
          { headers },
        ),
      ]);
      const activeTodos = (todosRes.data.todos || []).filter(
        (t) => !t.isArchivedThread && !isRepresentedInWaitingForYou(t),
      );
      const pendingArtifacts = (artifactsRes.data.artifacts || []).filter(
        (a) => a.artifactType !== 'blank_todo',
      );
      setActivityCount(activeTodos.length + pendingArtifacts.length);
    } catch (err) {
      console.error('Error fetching activity count:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchActivityCount();
  }, [fetchActivityCount]);

  // Debounced refresh — Pusher events can arrive in bursts
  const activityRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const refreshActivityCount = useCallback(() => {
    if (activityRefreshTimerRef.current) {
      clearTimeout(activityRefreshTimerRef.current);
    }
    activityRefreshTimerRef.current = setTimeout(() => {
      activityRefreshTimerRef.current = null;
      fetchActivityCount();
    }, 500);
  }, [fetchActivityCount]);

  useEffect(() => {
    return () => {
      if (activityRefreshTimerRef.current) {
        clearTimeout(activityRefreshTimerRef.current);
      }
    };
  }, []);

  // Pusher: sub-thread (in-flight) events
  const pusherCtx = usePusher(true) as any;
  const pusherChannel = pusherCtx?.pusherChannel ?? null;

  useEffect(() => {
    if (!pusherChannel) return;
    pusherChannel.bind(
      AgentStreamEvents.SUB_THREAD_CREATED,
      refreshActivityCount,
    );
    pusherChannel.bind(
      AgentStreamEvents.SUB_THREAD_UPDATE,
      refreshActivityCount,
    );
    return () => {
      pusherChannel.unbind(
        AgentStreamEvents.SUB_THREAD_CREATED,
        refreshActivityCount,
      );
      pusherChannel.unbind(
        AgentStreamEvents.SUB_THREAD_UPDATE,
        refreshActivityCount,
      );
    };
  }, [pusherChannel, refreshActivityCount]);

  // Pusher: artifact (waiting-for-you) events
  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();

  useEffect(() => {
    const cbId = 'uni-page-activity-count';
    registerCreatedCallback(cbId, refreshActivityCount);
    registerUpdatedCallback(cbId, refreshActivityCount);
    return () => {
      unregisterCreatedCallback(cbId);
      unregisterUpdatedCallback(cbId);
    };
  }, [
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
    refreshActivityCount,
  ]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        // Reminders count (only if not managed by Todoist)
        const [userTodosRes, todoistRes] = await Promise.all([
          api.get<GetUserTodosResponse>(
            `/user-todos?assigneeType=user${activeAgent?.id ? `&agentId=${activeAgent.id}` : ''}`,
            { headers },
          ),
          api.get<{ linkedAccounts: { id: number }[] }>('/todoist-link', {
            headers,
          }),
        ]);
        const isTodoistConnected = todoistRes.data.linkedAccounts.length > 0;
        setTodoistConnected(isTodoistConnected);
        if (!isTodoistConnected) {
          const activeTodosCount = (userTodosRes.data.todos || []).filter(
            (t) => t.status !== 'complete',
          ).length;
          setRemindersCount(activeTodosCount);
        } else {
          setRemindersCount(0);
        }
      } catch (err) {
        console.error('Error fetching tab counts:', err);
      }
    };
    fetchCounts();
  }, [getAccessTokenSilently, activeAgent?.id]);

  return (
    <div className='uni-page'>
      <div className='uni-page-left' onScroll={handleLeftScroll}>
        <UniNav
          activeTab={activeTab}
          onTabChange={handleTabChange}
          activityCount={activityCount}
          remindersCount={remindersCount}
          scrolled={navScrolled}
        />
        {activeTab === 'home' && creatingRoutine ? (
          <RoutineCreateView
            onBack={handleCreateRoutineBack}
            onRoutineCreated={handleRoutineCreated}
          />
        ) : activeTab === 'home' &&
          viewingRoutineDetail &&
          routineAutopilotId ? (
          <RoutineDetailView
            autopilotId={routineAutopilotId}
            routineName={routineNameParam}
            onBack={handleRoutineDetailBack}
          />
        ) : activeTab === 'home' &&
          viewingSampleRoutine &&
          selectedSampleRoutine ? (
          <SampleRoutineDetailView
            sampleRoutine={selectedSampleRoutine}
            onBack={handleSampleRoutineBack}
            onSetUp={handleSetUpSampleRoutine}
            settingUp={settingUp}
            onRoutineCreated={handleRoutineCreated}
          />
        ) : (
          <>
            {activeTab === 'home' && <WelcomeHeader />}
            {activeTab === 'home' && <ProjectsSection />}
            {activeTab === 'home' && (
              <RoutinesSection
                onCreateRoutine={handleCreateRoutine}
                onViewRoutine={handleRoutineCreated}
                onViewSampleRoutine={handleViewSampleRoutine}
              />
            )}
            {activeTab === 'activity' && (
              <ActivityPage onActivityChanged={fetchActivityCount} />
            )}
            {activeTab === 'reminders' && <RemindersPage />}
            {activeTab === 'documents' && <DocumentsPage />}
          </>
        )}
      </div>
      <DailyBriefingDrawer
        open={viewingDailyBriefing}
        onClose={handleDailyBriefingClose}
        onViewRoutine={handleViewRoutineFromBriefing}
      />
      <div className='uni-page-right'>
        <ThreadCallProvider>
          <header className='uni-page-chat-header'>
            <div className='uni-page-chat-header-left'>
              {activeAgent?.image_url && (
                <div className='uni-page-chat-header-avatar'>
                  <img
                    src={activeAgent.image_url}
                    alt={activeAgent.nickname || activeAgent.name || ''}
                  />
                </div>
              )}
              <span className='uni-page-chat-header-name'>
                {activeAgent?.nickname || activeAgent?.name}
              </span>
              <button
                type='button'
                className={`uni-page-chat-header-briefing-btn${viewingDailyBriefing ? ' active' : ''}`}
                onClick={handleToggleDailyBriefing}
              >
                <SunSettingIcon />
                <span>Daily Briefing</span>
              </button>
            </div>
            <div className='uni-page-chat-header-right'>
              <UniPageHeaderCallButton />
              {messagingState.whatsapp.loaded &&
                (messagingState.whatsapp.connected ? (
                  <span className='uni-page-chat-header-connected'>
                    <WhatsappIcon className='uni-page-chat-header-wa-icon' />
                    Connected
                  </span>
                ) : (
                  <button
                    type='button'
                    className='uni-page-chat-header-wa-btn'
                    onClick={handleConnectWhatsapp}
                  >
                    <WhatsappIcon className='uni-page-chat-header-wa-icon' />
                    Connect WhatsApp
                  </button>
                ))}
            </div>
          </header>

          {loading ? (
            <div className='uni-page-loading'>Loading...</div>
          ) : homeData ? (
            <ChatCanvasProvider>
              <ThreadViewContainer
                threadId={homeData.threadId}
                agentId={homeData.agentId}
              />
            </ChatCanvasProvider>
          ) : null}
        </ThreadCallProvider>

        {/* WhatsApp Registration Code Modal */}
        <Modal
          show={showWhatsappModal}
          onHide={() => setShowWhatsappModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Connect WhatsApp</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='whatsapp-reg-modal'>
              {regCodeLoading ? (
                <div className='whatsapp-reg-loading'>
                  <Spinner animation='border' size='sm' />
                  <p>Generating registration code...</p>
                </div>
              ) : regCodeError ? (
                <div className='whatsapp-reg-error'>{regCodeError}</div>
              ) : registrationData ? (
                <div className='whatsapp-reg-content'>
                  <QRCodeSVG value={registrationData.whatsapp_url} size={200} />
                  <p className='whatsapp-reg-code'>
                    Registration Code: <strong>{registrationData.code}</strong>
                  </p>
                  <p className='whatsapp-reg-instructions'>
                    Scan this QR code with your phone to open WhatsApp and send
                    the registration code. Or manually send the code to our
                    WhatsApp number.
                  </p>
                  <p className='whatsapp-reg-expiry'>
                    Code expires in 24 hours.
                  </p>
                </div>
              ) : null}
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default UniPage;
